import { getData } from '../CommonElements/Functions/PostGetFun';
import { API } from '../api/API';
import { saveCustomTabs } from './customTabs';
import { updateLabelsFromAPI, fetchTranslatedLabels } from './CommonLable';

export { fetchTranslatedLabels };

const systemConfigStore: Record<string, any> = {};

const SYSTEM_CONFIG_CACHE_KEY = "@system_config_key";

/**
 * Get system configuration value by key, or return the whole config object if no key specified
 * @param key Optional configuration key (e.g. "enable_stp_module")
 * @returns Config value or full dictionary
 */
export function getSystemConfig(key?: string): any {
  if (Object.keys(systemConfigStore).length === 0) {
    loadPersistedSystemConfig();
  }
  if (!key) return systemConfigStore;
  return systemConfigStore[key];
}

/**
 * Check if a system configuration key is enabled ('on', 'yes', 'true', '1', 1)
 * @param key Configuration key (e.g. "holiday_approval_enable")
 * @returns boolean
 */
export function isSystemConfigEnabled(key: string): boolean {
  const val = getSystemConfig(key);
  if (val === undefined || val === null) return false;
  const strVal = String(val).trim().toLowerCase();
  return ['on', 'yes', 'true', '1'].includes(strVal) || val === 1 || val === true;
}

/**
 * Save system configuration to localStorage
 */
export function saveSystemConfigToStorage(): void {
  try {
    localStorage.setItem(SYSTEM_CONFIG_CACHE_KEY, JSON.stringify(systemConfigStore));
  } catch (error) {
    console.error("Error saving system config to storage:", error);
  }
}

/**
 * Load persisted system configuration from localStorage on startup
 */
export function loadPersistedSystemConfig(): boolean {
  try {
    const stored = localStorage.getItem(SYSTEM_CONFIG_CACHE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      Object.assign(systemConfigStore, parsed);
      return true;
    }
  } catch (error) {
    console.error("Error loading persisted system config:", error);
  }
  return false;
}

// Auto-load on module import
loadPersistedSystemConfig();

/**
 * Clear persisted system config (on logout)
 */
export function clearPersistedSystemConfig(): void {
  try {
    Object.keys(systemConfigStore).forEach((key) => delete systemConfigStore[key]);
    localStorage.removeItem(SYSTEM_CONFIG_CACHE_KEY);
  } catch (error) {
    console.error("Error clearing system config:", error);
  }
}

/**
 * Process getSystemConfigs API response and convert into systemConfigStore map
 * API response structure contains: data.created, data.records, data.data, or data array
 */
export function updateSystemConfigFromAPI(response: any): Record<string, any> {
  if (!response) return systemConfigStore;

  let recordsList: any[] = [];

  const dataObj = response?.data?.data || response?.data || response;

  if (Array.isArray(dataObj?.created)) {
    recordsList = dataObj.created;
  } else if (Array.isArray(dataObj?.records)) {
    recordsList = dataObj.records;
  } else if (Array.isArray(dataObj?.data)) {
    recordsList = dataObj.data;
  } else if (Array.isArray(dataObj)) {
    recordsList = dataObj;
  }

  if (recordsList && recordsList.length > 0) {
    recordsList.forEach((item: any) => {
      if (!item || !item.ConfigKey) return;
      systemConfigStore[item.ConfigKey] = item.ConfigValue;
    });

    saveSystemConfigToStorage();
  }

  return systemConfigStore;
}

/**
 * Fetch Watermelon DB Bridge getSystemConfigs, getCustomTabs & getTranslatedLabels APIs concurrently.
 * Used during login and on hard page refresh.
 */
export async function fetchSystemConfigsAndCustomTabs(
  sUrl?: string | null,
  token?: string | null,
  orgUnitId?: string | number | null
): Promise<{ sysConfigsRes: any; customTabsRes: any; transLabelsRes?: any } | undefined> {
  const rawServerUrl = sUrl || localStorage.getItem('serverUrl') || 'https://sfa.uat.nitiapp.com/';
  const serverUrl = rawServerUrl.endsWith('/') ? rawServerUrl : `${rawServerUrl}/`;
  const resolvedToken =
    token ||
    localStorage.getItem('appToken') ||
    localStorage.getItem('apptoken') ||
    localStorage.getItem('token');

  if (!resolvedToken) return;

  const resolvedOrgUnitId =
    orgUnitId ??
    localStorage.getItem('orgUnitId') ??
    localStorage.getItem('org_unit_id') ??
    '';

  try {
    const transLabelsParams: Record<string, any> = {};
    if (resolvedOrgUnitId !== '' && resolvedOrgUnitId !== undefined && resolvedOrgUnitId !== null) {
      transLabelsParams.orgunit_id = resolvedOrgUnitId;
    }

    const [sysConfigsRes, customTabsRes, transLabelsRes] = await Promise.all([
      getData(`${serverUrl}${API.getSystemConfigs}`, true, { page: 1, limit: 200 }).catch((err) => {
        console.error("Error fetching getSystemConfigs:", err);
        return null;
      }),
      getData(`${serverUrl}${API.getCustomTabs}`, true, { apptoken: resolvedToken, page: 1, limit: 200 }).catch((err) => {
        console.error("Error fetching getCustomTabs:", err);
        return null;
      }),
      getData(`${serverUrl}${API.getTranslatedLabels}`, true, transLabelsParams).catch((err) => {
        console.error("Error fetching getTranslatedLabels:", err);
        return null;
      }),
    ]);

    if (sysConfigsRes) {
      console.log("getSystemConfigs response:", sysConfigsRes);
      if (sysConfigsRes?.status === 200 || sysConfigsRes?.data?.statusCode === 200) {
        updateSystemConfigFromAPI(sysConfigsRes);
      }
    }

    if (customTabsRes) {
      console.log("getCustomTabs response:", customTabsRes);
      if (customTabsRes?.status === 200 || customTabsRes?.data?.statusCode === 200) {
        saveCustomTabs(customTabsRes);
      }
    }

    if (transLabelsRes) {
      console.log("getTranslatedLabels response:", transLabelsRes);
      if (transLabelsRes?.status === 200 || transLabelsRes?.data?.statusCode === 200) {
        updateLabelsFromAPI(transLabelsRes);
      }
    }

    return { sysConfigsRes, customTabsRes, transLabelsRes };
  } catch (err) {
    console.error("Error fetching getSystemConfigs, getCustomTabs, or getTranslatedLabels:", err);
  }
}

